import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  TransactionHeader,
  TransactionNotes,
  TransactionDetailsCard,
  CurrencyAmountCard,
  TransactionInfoCard,
  copyToClipboard,
  LoadingPage,
  DepositStatus,
  Badge,
  CompanyRoleBadge,
  FlaggedBadgeIcon,
  ApproveConfirmationDialog,
  RejectConfirmationDialog,
  TransactionError,
  FallbackCompanyIcon,
  FallbackGroupIcon,
} from '@valiant/ui-shared'
import {
  useGetDepositTransactionQuery,
  useApproveDepositMutation,
  useRejectDepositMutation,
  useFlagDepositMutation,
  useCreateDepositNoteMutation,
  useGetCompanyQuery,
} from '@/generated/graphql'
import { toast } from 'sonner'
import { useAuth } from '@/AuthProvider'
import { MainLayout } from '@/components/layout/MainLayout'

export function DepositTransaction() {
  const { depositId } = useParams()
  const navigate = useNavigate()
  const [, setCopiedField] = useState<string | null>(null)
  const [isFlagged, setIsFlagged] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [status, setStatus] = useState<DepositStatus>(DepositStatus.Initiated)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isFlagging, setIsFlagging] = useState(false)
  const [isSendingNote, setIsSendingNote] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'

  // GraphQL query for deposit transaction data
  const [{ data: depositData, fetching: depositLoading, error: depositError }, refetch] =
    useGetDepositTransactionQuery({
      variables: { transactionId: depositId! },
      pause: !depositId,
    })

  const [{ data: companyData, fetching: companyLoading, error: companyError }] = useGetCompanyQuery(
    {
      variables: { companyId: depositData?.getDepositTransaction?.companyId || '' },
      pause: !depositData?.getDepositTransaction?.companyId,
    },
  )

  // GraphQL mutations
  const [, approveDeposit] = useApproveDepositMutation()
  const [, rejectDeposit] = useRejectDepositMutation()
  const [, flagDeposit] = useFlagDepositMutation()
  const [, createDepositNote] = useCreateDepositNoteMutation()

  const depositTransaction = depositData?.getDepositTransaction

  // Set initial state from GraphQL data
  useEffect(() => {
    if (depositTransaction) {
      setStatus(depositTransaction.status)
      setIsFlagged(depositTransaction.isFlagged)
    }
  }, [depositTransaction])

  // Track when initial data has been loaded
  useEffect(() => {
    if (depositTransaction && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [depositTransaction, hasInitiallyLoaded])

  // Ref to store the interval ID for cleanup
  const refetchIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-refetch for non-finalized transactions (excluding pending/requested state)
  useEffect(() => {
    // Clear any existing interval
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
      refetchIntervalRef.current = null
    }

    // For deposits, we only refetch when approved and potentially still processing
    // Pending is similar to "requested" state, so we exclude it
    const shouldRefetch = depositTransaction && depositTransaction.finalizedDate === null

    if (shouldRefetch) {
      // Refetch every 5 seconds for approved deposits that might still be processing
      refetchIntervalRef.current = setInterval(() => {
        refetch({ requestPolicy: 'network-only' })
      }, 5000)
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current)
        refetchIntervalRef.current = null
      }
    }
  }, [status, depositTransaction, refetch])

  const handleCopy = async (text: string, field: string) => {
    const success = await copyToClipboard(text)

    if (success) {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
      toast.success('Copied to clipboard')
    }
  }

  const handleApprove = () => {
    setShowApproveDialog(true)
  }

  const handleReject = () => {
    setShowRejectDialog(true)
  }

  const handleFlagToggle = async (flagged: boolean) => {
    if (!depositId || isFlagging) return

    setIsFlagging(true)
    try {
      const result = await flagDeposit({ input: { depositId: depositId, flagged } })
      if (result.error) {
        toast.error(`Failed to ${flagged ? 'flag' : 'unflag'} transaction`)
        console.error('Error flagging transaction:', result.error)
        return
      }

      toast.success(`Transaction ${flagged ? 'flagged' : 'unflagged'} successfully`)
      setIsFlagged(flagged)
      // The cache should automatically update due to the mutation response
    } catch (error) {
      toast.error(`Failed to ${flagged ? 'flag' : 'unflag'} transaction`)
      console.error('Error flagging transaction:', error)
      // Reset flag state on error
      setIsFlagged(!flagged)
    } finally {
      setIsFlagging(false)
    }
  }

  const confirmApprove = useCallback(async () => {
    if (!depositId || isApproving) return

    setIsApproving(true)
    try {
      const result = await approveDeposit({ depositId: depositId })
      if (result.error) {
        toast.error('Failed to approve deposit')
        console.error('Error approving deposit:', result.error)
        return
      }

      toast.success('Deposit approved successfully')
      setStatus(DepositStatus.Approved)
      setShowApproveDialog(false)
      // The cache should automatically update due to the mutation response and refresh the page data
    } catch (error) {
      toast.error('Failed to approve deposit')
      console.error('Error approving deposit:', error)
      // Keep dialog open on error
    } finally {
      setIsApproving(false)
    }
  }, [approveDeposit, depositId, isApproving])

  const confirmReject = useCallback(async () => {
    if (!depositId || isRejecting) return

    setIsRejecting(true)
    try {
      const result = await rejectDeposit({ depositId: depositId, reason: 'Rejected by admin' })
      if (result.error) {
        toast.error('Failed to reject deposit')
        console.error('Error rejecting deposit:', result.error)
        return
      }

      toast.success('Deposit rejected successfully')
      setStatus(DepositStatus.Rejected)
      setShowRejectDialog(false)
      // The cache should automatically update due to the mutation response and refresh the page data
    } catch (error) {
      toast.error('Failed to reject deposit')
      console.error('Error rejecting deposit:', error)
      // Keep dialog open on error
    } finally {
      setIsRejecting(false)
    }
  }, [rejectDeposit, depositId, isRejecting])

  const handleSendNote = async () => {
    if (!newNote.trim() || !depositId || isSendingNote) return

    setIsSendingNote(true)
    try {
      const result = await createDepositNote({
        input: {
          transactionId: depositId,
          content: newNote.trim(),
        },
      })

      if (result.error) {
        toast.error('Failed to add note')
        console.error('Error creating note:', result.error)
        return
      }

      toast.success('Note added successfully')
      setNewNote('')
      // The cache should automatically update and refresh the notes list
    } catch (error) {
      toast.error('Failed to add note')
      console.error('Error creating note:', error)
    } finally {
      setIsSendingNote(false)
    }
  }

  const modalsComponent = useMemo(() => {
    return (
      <>
        <ApproveConfirmationDialog
          open={showApproveDialog}
          onOpenChange={setShowApproveDialog}
          onConfirm={confirmApprove}
          isSubmitting={isApproving}
          title="Approve Transaction"
          description="Are you sure you want to approve this deposit transaction? This action cannot be undone."
        />
        <RejectConfirmationDialog
          open={showRejectDialog}
          onOpenChange={setShowRejectDialog}
          onConfirm={confirmReject}
          isSubmitting={isRejecting}
          title="Reject Transaction"
          description="Are you sure you want to reject this deposit transaction? This action cannot be undone."
        />
      </>
    )
  }, [showApproveDialog, showRejectDialog, isApproving, isRejecting, confirmApprove, confirmReject])

  const badges = useMemo(() => {
    return [
      {
        node: <CompanyRoleBadge tradeType={companyData?.getCompany?.tradeType || ''} />,
        direction: 'LEFT',
      },
      {
        node: (
          <Badge variant="outline" className="text-xs ">
            {depositTransaction?.group}
          </Badge>
        ),
        direction: 'LEFT',
      },
    ]
  }, [companyData, depositTransaction])

  // Loading state - only show on first load
  if ((depositLoading || companyLoading) && !hasInitiallyLoaded) {
    return <LoadingPage />
  }

  // Error state
  if (depositError || !depositTransaction || companyError) {
    return (
      <TransactionError
        message={depositError?.message || companyError?.message || ''}
        title="Transaction Not Found"
      />
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: depositTransaction.group,
          onClick: () => navigate(`/groups/${depositTransaction.groupId}`),
          imageUrl: depositTransaction.groupImageUrl || '',
          fallbackImage: <FallbackGroupIcon />,
        },
        {
          label: depositTransaction.company,
          onClick: () => navigate(`/companies/${depositTransaction.companyId}`),
          imageUrl: depositTransaction.companyImageUrl || '',
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      badges={badges}
      modalsComponent={modalsComponent}
      subBarOverride={
        <TransactionHeader
          transactionId={depositId || ''}
          status={status}
          transactionType="DEPOSIT"
          showActions={true}
          onApprove={handleApprove}
          onReject={handleReject}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isComplianceUser={isComplianceUser}
          isFlagged={depositTransaction.isFlagged}
          isExternalApi={depositTransaction.isExternalApi}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Transaction Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Transaction Overview Header */}
          <div className="space-y-4">
            <h2 className="hidden md:block text-xl font-semibold">Transaction Overview</h2>
            <h3 className="text-2xl md:text-4xl font-normal md:font-semibold md:text-main flex gap-2 items-center">
              <div className="md:hidden">{isFlagged ? <FlaggedBadgeIcon /> : null}</div>
              Deposit
            </h3>
          </div>

          {/* Transaction Overview - Side by Side Cards */}
          <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
            <CurrencyAmountCard
              company={depositTransaction.company}
              companyImage={depositTransaction.companyImageUrl || ''}
              amount={depositTransaction.amount}
              currency={depositTransaction.currency}
              symbol={depositTransaction.symbol}
              chain={depositTransaction.chain}
              label="Deposit"
            />

            <TransactionInfoCard
              user={depositTransaction.user}
              company={depositTransaction.company}
              group={depositTransaction.group}
              decisionBy={depositTransaction.decisionBy}
              transactionId={depositId || ''}
              creationDate={depositTransaction.creationDate}
              decisionDate={depositTransaction.decisionDate}
              finalizedDate={depositTransaction.finalizedDate}
              onCopy={handleCopy}
            />
          </div>

          <TransactionDetailsCard
            title="Details"
            fields={[
              {
                label: 'Deposit Transaction Hash',
                value: depositTransaction.depositTransactionHash,
                fieldKey: 'hash',
              },
              {
                label: 'Deposit Address',
                value: depositTransaction.depositAddress,
                fieldKey: 'address',
              },
            ]}
            onCopy={handleCopy}
            chain={depositTransaction.chain}
          />
        </div>

        {/* Right Column - Notes */}
        <div className="space-y-6">
          <TransactionNotes
            notes={depositTransaction.notes || []}
            newNote={newNote}
            onNewNoteChange={setNewNote}
            onSendNote={handleSendNote}
            isSendingNote={isSendingNote}
            isFlagged={isFlagged}
            onFlagToggle={handleFlagToggle}
            isFlagging={isFlagging}
            showFlagToggle={true}
          />
        </div>
      </div>
    </MainLayout>
  )
}
